import { firestore } from "@/services/firestore";
import { storage } from "@/services/storage";
import { getDownloadURL, ref, StorageError } from "firebase/storage";
import { StateCreator } from "zustand";
import { employee } from "@/repositories/employee";
import { customerUpdate } from "@/repositories/customerUpdates";
import { localStorageManager } from "@/services/localStorage";
import {
  featureFlags,
  isShopDocScopedFlag,
  getShopDocScopedValue,
  FEATURE_FLAG,
} from "@/repositories/featureFlags";
import { sentry } from "@/services/sentry";
import { UserSlice } from "./user.slice";
import { FeatureFlagEnabled } from "@/types/v1";
import { httpsCallables } from "@/repositories/httpsCallables";
import {
  Card,
  Employee,
  SyncDriver,
  Shop,
  Integration,
  CustomerUpdate,
  EmployeeRole,
  ActivityFeed,
  LOCAL_STORAGE_BASE_KEYS,
  LabelSyncStatusDoc,
} from "@/types/v2";
import { firestoreToDateTime, safeJsonParse } from "@/lib/utils";
import { resolveShopLogo } from "@/lib/shopLogo";
import { getInitialCardZoom, persistCardZoom } from "@/lib/cardZoom";
import {
  getInitialCompactContainerHeaders,
  persistCompactContainerHeaders,
} from "@/lib/containerHeader";
import { card } from "@/repositories/cards";
import { shop } from "@/repositories/shop";
import { user } from "@/repositories/user";
import { AuthSlice } from "@/stores/auth.slice";
import { ActivityFeedRepository } from "@/repositories/activityFeed";
import { TimeInContainerMetric } from "@/types/v2/TimeInContainer";
import { TimeInContainerMetricsRepository } from "@/repositories/timeInContainerMetrics";
import { Conversation } from "@/types/v2/Conversation";
import { ConversationRepository } from "@/repositories/conversation";
import { ListenersSlice } from "@/stores/listeners.slice";
import { analyticsRepository } from "@/repositories/analytics";
import { TagsDocument } from "@/types/v2/Tags";
import { TagsRepository } from "@/repositories/tags";
import { CustomerUpdatesSlice } from "@/stores/customerUpdates.slice";
import { TaskSlice } from "@/stores/task.slice";

const SHOP_DOC_SCOPED_FLAGS =
  Object.values(FEATURE_FLAG).filter(isShopDocScopedFlag);

export interface ShopSlice {
  shop: Shop | undefined;
  shopId: string | undefined;
  integration: Integration | undefined;
  setIntegration: (shopData: Shop) => void;
  logoUrl: string | undefined;
  // Which shop `logoUrl` was resolved for. Shops switch without the store
  // being torn down, so an unscoped URL leaks across shops.
  logoShopId: string | undefined;
  setShopRole: (role: EmployeeRole) => void;
  subscribeShop: (id: string) => void;
  getLogoUrl: () => Promise<void>;
  dashboardServiceWriterId: number | string | string[] | undefined;
  setDashboardServiceWriterId: (id: string | string[]) => void;
  dashboardTechnicianId: number | string | string[] | undefined;
  setDashboardTechnicianId: (id: string | string[]) => void;
  dashboardDueInStart: string | undefined | null;
  setDashboardDueInStart: (dueIn: string | undefined | null) => void;
  dashboardDueInEnd: string | undefined | null;
  setDashboardDueInEnd: (dueIn: string | undefined | null) => void;
  dashboardDueOutStart: string | undefined | null;
  setDashboardDueOutStart: (dueOut: string | undefined | null) => void;
  dashboardDueOutEnd: string | undefined | null;
  setDashboardDueOutEnd: (dueOut: string | undefined | null) => void;
  dashboardApptStartStart: string | undefined | null;
  setDashboardApptStartStart: (apptStart: string | undefined | null) => void;
  dashboardApptStartEnd: string | undefined | null;
  setDashboardApptStartEnd: (apptStart: string | undefined | null) => void;
  dashboardApptEndStart: string | undefined | null;
  setDashboardApptEndStart: (apptEnd: string | undefined | null) => void;
  dashboardApptEndEnd: string | undefined | null;
  setDashboardApptEndEnd: (apptEnd: string | undefined | null) => void;
  dashboardLayoutId: string | undefined;
  setDashboardSearchString: (searchString: string) => void;
  dashboardSearchString: string;
  setDashboardLayoutId: (id: string) => void;
  dashboardCardZoom: number;
  setDashboardCardZoom: (zoom: number) => void;
  dashboardCompactContainerHeaders: boolean;
  setDashboardCompactContainerHeaders: (compact: boolean) => void;
  dashboardTags: string[] | undefined | null;
  setDashboardTags: (tags: string[] | undefined | null) => void;
  dashboardColors: string[] | undefined | null;
  setDashboardColors: (colors: string[] | undefined | null) => void;
  dashboardContainer: string[] | undefined | null;
  setDashboardContainer: (containerIds: string[] | undefined | null) => void;
  refreshTimeInSeconds: number;
  isSyncing: boolean;
  lastSyncedTime: number;
  syncButtonText: string | undefined;
  syncError: boolean;
  employees: Employee[] | undefined;
  /**
   * Same snapshot as `employees` but keeping soft-deleted docs (integration
   * `disabled` ones are still dropped). Only the super-admin "Show deleted"
   * view of the Employees table reads this; everything else uses `employees`.
   */
  employeesIncludingDeleted: Employee[];
  employeesLoaded: boolean;
  subscribeEmployees: () => void;
  customerUpdates: CustomerUpdate[];
  subscribeCustomerUpdates: () => void;
  setCurrentShopId: (id: string | null) => void;
  featureFlags: FeatureFlagEnabled | undefined;
  setFeatureFlags: () => void;
  syncWithSource: (force?: boolean, fullReSync?: boolean) => void;
  syncDriver: SyncDriver | undefined;
  subscribeSyncDriver: () => void;
  labelSyncStatus: LabelSyncStatusDoc | undefined;
  subscribeLabelSyncStatus: () => void;
  layouts: Shop["layouts"];
  defaultTimer: Shop["defaultTimer"];
  cardContents: Shop["cardConfig"]["cardContents"];
  statuses: Shop["statusesToSync"];
  lastSyncedTimestamp: Shop["lastSyncedTimestamp"];
  refreshRate: Shop["refreshTimeInSeconds"];
  cards: Card[];
  subscribeCards: (ids: string[]) => void;
  setCard: (card: Card) => void;
  removeCard: (id: string) => void;
  shopRole: EmployeeRole | "superAdmin" | undefined;
  activityFeed: ActivityFeed[];
  subscribeActivityFeed: () => void;
  timeInContainerMetrics: TimeInContainerMetric[];
  subscribeTimeInContainerMetrics: () => void;
  conversations: Conversation[];
  conversationsLoaded: boolean;
  subscribeConversations: () => void;
  tags: TagsDocument | undefined;
  subscribeTags: () => void;
}

const initialShopSlice: ShopSlice = {
  shop: undefined,
  integration: undefined,
  setIntegration: () => {},
  shopId: undefined,
  logoUrl: undefined,
  logoShopId: undefined,
  setShopRole: () => {},
  subscribeShop: () => {},
  getLogoUrl: async () => {},
  dashboardServiceWriterId: "all",
  setDashboardServiceWriterId: () => {},
  dashboardTechnicianId: "all",
  setDashboardTechnicianId: () => {},
  dashboardDueInStart: undefined,
  setDashboardDueInStart: () => {},
  dashboardDueInEnd: undefined,
  setDashboardDueInEnd: () => {},
  dashboardDueOutStart: undefined,
  setDashboardDueOutStart: () => {},
  dashboardDueOutEnd: undefined,
  setDashboardDueOutEnd: () => {},
  dashboardApptStartStart: undefined,
  setDashboardApptStartStart: () => {},
  dashboardApptStartEnd: undefined,
  setDashboardApptStartEnd: () => {},
  dashboardApptEndStart: undefined,
  setDashboardApptEndStart: () => {},
  dashboardApptEndEnd: undefined,
  setDashboardApptEndEnd: () => {},
  dashboardLayoutId: undefined,
  setDashboardLayoutId: () => {},
  dashboardCardZoom: getInitialCardZoom(),
  setDashboardCardZoom: () => {},
  dashboardCompactContainerHeaders: getInitialCompactContainerHeaders(),
  setDashboardCompactContainerHeaders: () => {},
  setDashboardSearchString: () => {},
  dashboardSearchString: "",
  dashboardTags: undefined,
  setDashboardTags: () => {},
  dashboardColors: undefined,
  setDashboardColors: () => {},
  dashboardContainer: undefined,
  setDashboardContainer: () => {},
  refreshTimeInSeconds: 300,
  isSyncing: false,
  lastSyncedTime: 0,
  syncButtonText: "Sync",
  syncError: false,
  employees: [],
  employeesIncludingDeleted: [],
  employeesLoaded: false,
  subscribeEmployees: () => {},
  customerUpdates: [],
  subscribeCustomerUpdates: () => {},
  setCurrentShopId: () => {},
  featureFlags: undefined,
  setFeatureFlags: () => {},
  subscribeSyncDriver: () => {},
  syncDriver: undefined,
  labelSyncStatus: undefined,
  subscribeLabelSyncStatus: () => {},
  syncWithSource: () => {},
  layouts: [],
  defaultTimer: "",
  cardContents: {},
  statuses: [],
  lastSyncedTimestamp: 0,
  refreshRate: 300,
  cards: [],
  subscribeCards: () => {},
  setCard: () => {},
  removeCard: () => {},
  shopRole: undefined,
  activityFeed: [],
  subscribeActivityFeed: () => {},
  timeInContainerMetrics: [],
  subscribeTimeInContainerMetrics: () => {},
  conversations: [],
  conversationsLoaded: false,
  subscribeConversations: () => {},
  tags: undefined,
  subscribeTags: () => {},
};

export const createShopSlice: StateCreator<
  ShopSlice &
    UserSlice &
    AuthSlice &
    ListenersSlice &
    CustomerUpdatesSlice &
    TaskSlice,
  [],
  [],
  ShopSlice
> = (set, get) => ({
  ...initialShopSlice,
  setCurrentShopId: (id: string | null) => {
    localStorageManager.setItem(LOCAL_STORAGE_BASE_KEYS.SHOP_ID, id);
    set({ shopId: id === null ? undefined : id });

    if (id === "unverified" || id === null) {
      get().unsubscribeShopListener();
      return;
    } else {
      const user = get().user;
      sentry.setUser({ id: user?.id, email: user?.email, shopId: id });
      analyticsRepository.setAnalyticsUserProperty("shopId", id);
    }
    get().subscribeShop(id);
  },
  setDashboardServiceWriterId: (id) => {
    set({ dashboardServiceWriterId: id });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_SERVICE_ADVISOR_ID,
      JSON.stringify(id),
      shopId
    );
  },
  setDashboardTechnicianId: (id) => {
    set({ dashboardTechnicianId: id });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_TECHNICIAN_ID,
      JSON.stringify(id),
      shopId
    );
  },
  setDashboardDueInStart: (dueIn) => {
    set({ dashboardDueInStart: dueIn });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_DUE_IN_START,
      dueIn,
      shopId
    );
  },
  setDashboardDueInEnd: (dueIn) => {
    set({ dashboardDueInEnd: dueIn });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_DUE_IN_END,
      dueIn,
      shopId
    );
  },
  setDashboardDueOutStart: (dueOut) => {
    set({ dashboardDueOutStart: dueOut });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_DUE_OUT_START,
      dueOut,
      shopId
    );
  },
  setDashboardDueOutEnd: (dueOut) => {
    set({ dashboardDueOutEnd: dueOut });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_DUE_OUT_END,
      dueOut,
      shopId
    );
  },
  setDashboardApptStartStart: (apptStart) => {
    set({ dashboardApptStartStart: apptStart });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_APPT_START_START,
      apptStart,
      shopId
    );
  },
  setDashboardApptStartEnd: (apptStart) => {
    set({ dashboardApptStartEnd: apptStart });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_APPT_START_END,
      apptStart,
      shopId
    );
  },
  setDashboardApptEndStart: (apptEnd) => {
    set({ dashboardApptEndStart: apptEnd });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_APPT_END_START,
      apptEnd,
      shopId
    );
  },
  setDashboardApptEndEnd: (apptEnd) => {
    set({ dashboardApptEndEnd: apptEnd });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_APPT_END_END,
      apptEnd,
      shopId
    );
  },
  setDashboardLayoutId: (id) => {
    set({ dashboardLayoutId: id });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.DASHBOARD_LAYOUT_ID,
      id,
      shopId
    );
  },
  setDashboardCardZoom: (zoom) => {
    set({ dashboardCardZoom: zoom });
    persistCardZoom(zoom);
  },
  setDashboardCompactContainerHeaders: (compact) => {
    set({ dashboardCompactContainerHeaders: compact });
    persistCompactContainerHeaders(compact);
  },
  setDashboardTags: (tags: string[] | undefined | null) => {
    set({ dashboardTags: tags });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_TAGS,
      JSON.stringify(tags),
      shopId
    );
  },
  setDashboardColors: (colors: string[] | undefined | null) => {
    set({ dashboardColors: colors });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_COLORS,
      JSON.stringify(colors),
      shopId
    );
  },
  setDashboardContainer: (containerIds: string[] | undefined | null) => {
    set({ dashboardContainer: containerIds });
    const shopId = get().shopId;
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.FILTER_BY_CONTAINER,
      JSON.stringify(containerIds),
      shopId
    );
  },
  setDashboardSearchString: (searchString) => {
    set({ dashboardSearchString: searchString });
  },
  subscribeSyncDriver: () => {
    const shopId = get().shopId;
    if (!shopId) return;
    const unsubscribe = firestore.subscribeDocument<SyncDriver>(
      `shops/${shopId}/sync/syncdriver`,
      (doc) => {
        set({
          syncDriver: doc,
          lastSyncedTime: firestoreToDateTime(
            doc?.lastSuccessfulSyncAt
          )?.getTime(),
        });
      }
    );
    get().setOtherListener("syncDriver", unsubscribe);
  },
  subscribeLabelSyncStatus: () => {
    const shopId = get().shopId;
    if (!shopId) return;
    const unsubscribe = firestore.subscribeDocument<LabelSyncStatusDoc>(
      `shops/${shopId}/labelSync/status`,
      (doc) => {
        set({ labelSyncStatus: doc });
      }
    );
    get().setOtherListener("labelSyncStatus", unsubscribe);
  },
  setIntegration: (shopData: Shop) => {
    set({
      integration: shop.generateIntegration(shopData),
    });
  },
  setShopRole: (role: EmployeeRole) => {
    const authRole = get().authClaims;
    if (authRole?.superAdmin) {
      set({ shopRole: role });
    }
  },
  subscribeShop: async (id: string) => {
    const userShopRole = await user.getUserShopRole(id);
    if (!userShopRole) {
      // return;
    }

    // User has access;
    set({ shopRole: userShopRole ?? "admin" });

    let hasSetInitialFeatureFlags = false;
    const unsubscribe = shop.subscribeShop(id, (doc) => {
      const shopCards = doc?.containers?.flatMap((c) => c.tags) ?? [];
      get().subscribeCards(shopCards);

      const logo = resolveShopLogo(id, doc?.logoUrl, {
        logoUrl: get().logoUrl,
        logoShopId: get().logoShopId,
      });

      const previousEnabledFeatures = get().shop?.enabledFeatures;

      set({
        shop: doc,
        layouts: doc?.layouts ?? [],
        defaultTimer: doc?.defaultTimer ?? "",
        cardContents: doc?.cardConfig.cardContents ?? {},
        statuses: doc?.statusesToSync ?? [],
        lastSyncedTimestamp: doc?.lastSyncedTimestamp ?? 0,
        refreshRate: doc?.refreshTimeInSeconds ?? 300,
        logoUrl: logo.logoUrl,
        logoShopId: logo.logoShopId,
      });
      if (doc) {
        get().setIntegration(doc);

        const shopDocScopedFlagsChanged = SHOP_DOC_SCOPED_FLAGS.some(
          (flag) =>
            (previousEnabledFeatures?.[flag] ?? false) !==
            (doc.enabledFeatures?.[flag] ?? false)
        );
        if (!hasSetInitialFeatureFlags || shopDocScopedFlagsChanged) {
          hasSetInitialFeatureFlags = true;
          get().setFeatureFlags();
        }
      }
    });

    get().setDashboardServiceWriterId(
      safeJsonParse(
        localStorageManager.getItem(
          LOCAL_STORAGE_BASE_KEYS.FILTER_BY_SERVICE_ADVISOR_ID,
          id
        ),
        []
      )
    );
    get().setDashboardTechnicianId(
      safeJsonParse(
        localStorageManager.getItem(
          LOCAL_STORAGE_BASE_KEYS.FILTER_BY_TECHNICIAN_ID,
          id
        ),
        []
      )
    );

    get().setDashboardDueInStart(
      localStorageManager.getItem(
        LOCAL_STORAGE_BASE_KEYS.FILTER_BY_DUE_IN_START,
        id
      )
    );
    get().setDashboardDueInEnd(
      localStorageManager.getItem(
        LOCAL_STORAGE_BASE_KEYS.FILTER_BY_DUE_IN_END,
        id
      )
    );

    get().setDashboardDueOutStart(
      localStorageManager.getItem(
        LOCAL_STORAGE_BASE_KEYS.FILTER_BY_DUE_OUT_START,
        id
      )
    );
    get().setDashboardDueOutEnd(
      localStorageManager.getItem(
        LOCAL_STORAGE_BASE_KEYS.FILTER_BY_DUE_OUT_END,
        id
      )
    );
    get().setDashboardApptStartStart(
      localStorageManager.getItem(
        LOCAL_STORAGE_BASE_KEYS.FILTER_BY_APPT_START_START,
        id
      )
    );
    get().setDashboardApptStartEnd(
      localStorageManager.getItem(
        LOCAL_STORAGE_BASE_KEYS.FILTER_BY_APPT_START_END,
        id
      )
    );
    get().setDashboardApptEndStart(
      localStorageManager.getItem(
        LOCAL_STORAGE_BASE_KEYS.FILTER_BY_APPT_END_START,
        id
      )
    );
    get().setDashboardApptEndEnd(
      localStorageManager.getItem(
        LOCAL_STORAGE_BASE_KEYS.FILTER_BY_APPT_END_END,
        id
      )
    );
    get().setDashboardTags(
      safeJsonParse(
        localStorageManager.getItem(LOCAL_STORAGE_BASE_KEYS.FILTER_BY_TAGS, id),
        []
      )
    );
    get().setDashboardColors(
      safeJsonParse(
        localStorageManager.getItem(
          LOCAL_STORAGE_BASE_KEYS.FILTER_BY_COLORS,
          id
        ),
        []
      )
    );
    get().setDashboardContainer(
      safeJsonParse(
        localStorageManager.getItem(
          LOCAL_STORAGE_BASE_KEYS.FILTER_BY_CONTAINER,
          id
        ),
        []
      )
    );

    get().setDashboardLayoutId(
      localStorageManager.getItem(
        LOCAL_STORAGE_BASE_KEYS.DASHBOARD_LAYOUT_ID,
        id
      ) ?? "Main Layout"
    );
    get().setShopListener(unsubscribe);
    get().getLogoUrl();
    get().subscribeEmployees();
    get().subscribeCustomerUpdates();
    get().subscribeToAllMyCustomerUpdates();
    get().subscribeSyncDriver();
    get().subscribeLabelSyncStatus();
    get().subscribeActivityFeed();
    get().subscribeTimeInContainerMetrics();
    get().subscribeConversations();
    get().subscribeTags();
    get().subscribeTasks();
  },
  unsubscribeShop: () => {
    get().unsubscribeShopListener();

    get().unsubscribeOtherListeners();

    set({
      shop: undefined,
      integration: undefined,
      shopId: undefined,
      logoUrl: undefined,
      logoShopId: undefined,
      dashboardServiceWriterId: "all",
      dashboardTechnicianId: "all",
      dashboardLayoutId: undefined,
      dashboardSearchString: "",
      dashboardContainer: undefined,
      refreshTimeInSeconds: 300,
      isSyncing: false,
      lastSyncedTime: 0,
      syncButtonText: "Sync",
      syncError: false,
      employees: [],
      employeesIncludingDeleted: [],
      employeesLoaded: false,
      customerUpdates: [],
      featureFlags: undefined,
      syncDriver: undefined,
      labelSyncStatus: undefined,
      layouts: [],
      defaultTimer: "",
      cardContents: {},
      statuses: [],
      lastSyncedTimestamp: 0,
      refreshRate: 300,
      cards: [],
      shopRole: undefined,
      activityFeed: [],
      timeInContainerMetrics: [],
      conversations: [],
      conversationsLoaded: false,
      tags: undefined,
    });
  },
  getLogoUrl: async () => {
    const shopId = get().shopId;
    if (!shopId) {
      return;
    }

    // `get().shop` still holds the previous shop during a switch, so only
    // trust it when it is the shop we were asked about.
    // (`shop?.id === shopId` would not narrow `shop` for TypeScript, hence the
    // explicit `shop &&`.)
    //
    // This function is called synchronously right after `subscribeShop`
    // registers its listener, before that listener's first snapshot has
    // arrived, so `shop` is usually `undefined` (cold load) or still the
    // previous shop (a switch) - the two branches below only apply on a
    // re-subscribe to an already-loaded shop. `resolveShopLogo`, driven by
    // the snapshot, is the authoritative resolver for the current shop.
    const shop = get().shop;
    const docLogoUrl = shop && shop.id === shopId ? shop.logoUrl : undefined;
    if (docLogoUrl) {
      set({ logoUrl: docLogoUrl, logoShopId: shopId });
      return;
    }

    // An empty string is an explicit clear, same as `resolveShopLogo`'s
    // second case - `logo.delete()` writes "" rather than removing the
    // field. This only fires on the re-subscribe case above; on a cold load
    // or shop switch `docLogoUrl` is `undefined` regardless of what the doc
    // says, so a "" clear falls through to the Storage lookup below. If that
    // delete's fire-and-forget `deleteObject` call failed, Storage still has
    // the object and this can resolve it back - the next snapshot corrects
    // it again via `resolveShopLogo`.
    if (docLogoUrl === "") {
      set({ logoUrl: undefined, logoShopId: shopId });
      return;
    }

    try {
      const url = await getDownloadURL(ref(storage, `${shopId}/logo`));
      // The user may have switched shops while this resolved.
      if (get().shopId !== shopId) {
        return;
      }
      set({ logoUrl: url, logoShopId: shopId });
    } catch (error) {
      // A shop with no uploaded logo is the ordinary case, not an error - the
      // object simply does not exist. Anything else (permission-denied,
      // network failure, ...) is a genuine problem worth surfacing.
      const isObjectNotFound =
        error instanceof StorageError &&
        error.code === "storage/object-not-found";
      if (!isObjectNotFound) {
        sentry.captureException(error);
      }
      // The user may have switched shops while this resolved. Leave logoUrl
      // undefined so the header and Settings fall back to the Shop Hero
      // wordmark.
      if (get().shopId !== shopId) {
        return;
      }
      set({ logoUrl: undefined, logoShopId: shopId });
    }
  },
  syncWithSource: (force?: boolean, fullReSync?: boolean) => {
    const ff = get().featureFlags;
    if (ff && ff["isManualCardOnlyEnabled"]) {
      return;
    }
    const integration = get().integration;
    if (!integration) {
      return;
    }

    const shopId = get().shop?.id;
    if (!shopId) return;

    const syncType = fullReSync ? "fullReSync" : force ? "full" : "incremental";
    httpsCallables
      .requestSyncWithSource({ shopId, syncType })
      .catch((error: unknown) => {
        console.error(error);
      });
  },
  subscribeEmployees: () => {
    const shopId = get().shopId;
    if (!shopId) return;
    // Re-arm the hydration gate before (re)subscribing: until the first
    // snapshot for THIS shop lands, `employees` still holds the previous
    // shop's people. unsubscribeShop (the only other reset) is never called,
    // so on an in-session shop switch this is what re-gates consumers of
    // employeesLoaded (e.g. the automation import remap dialog).
    set({ employeesLoaded: false });
    const unsubscribe = employee.subscribeAll(shopId, (employees) =>
      set({
        employees: employees?.filter((e) => !e._deleted && !e.disabled) ?? [],
        employeesIncludingDeleted: employees?.filter((e) => !e.disabled) ?? [],
        employeesLoaded: true,
      })
    );
    get().setOtherListener("employees", unsubscribe);
  },
  subscribeCustomerUpdates: () => {
    const shopId = get().shopId;
    if (!shopId) return;

    const unsubscribe = customerUpdate.subscribeAll(shopId, (customerUpdates) =>
      set({ customerUpdates: customerUpdates ?? [] })
    );
    get().setOtherListener("customerUpdates", unsubscribe);
  },
  setFeatureFlags: async () => {
    const ff = await featureFlags.list();
    const enabledFf: FeatureFlagEnabled = {};
    const shopId = get().shopId;
    if (!shopId) return;
    const shopEnabledFeatures = get().shop?.enabledFeatures;
    SHOP_DOC_SCOPED_FLAGS.forEach((flag) => {
      enabledFf[flag] = getShopDocScopedValue(flag, shopEnabledFeatures);
    });
    if (ff) {
      Object.keys(ff).forEach((key) => {
        if (key in enabledFf) return;
        if (ff[key].allUsers) {
          enabledFf[key] = ff[key].allUsers;
        } else {
          enabledFf[key] = ff[key].testUsers.includes(shopId);
        }
      });
    }
    set({ featureFlags: enabledFf });
    analyticsRepository.setAnalyticsUserProperties({
      isTasksTabEnabled: !!enabledFf.isTasksTabEnabled,
      isContainerMetricsEnabled: !!enabledFf.isContainerMetricsEnabled,
      isDispatchViewEnabled: !!enabledFf.isDispatchViewEnabled,
      isScheduledAutomationsEnabled: !!enabledFf.isScheduledAutomationsEnabled,
      isPromiseTimeEnabled: !!enabledFf.isPromiseTimeEnabled,
    });
  },
  subscribeCards: (ids: string[]) => {
    const shopId = get().shopId;
    if (!shopId) return;

    let currentCards = [...get().cards];
    const currentCardIds = get().cards.map((c) => c.id);

    const cardsToRemove = currentCardIds.filter((id) => !ids.includes(id));
    const cardsToAdd = ids.filter((id) => !currentCardIds.includes(id));

    currentCards = currentCards.filter((c) => !cardsToRemove.includes(c.id));
    for (let i = 0; i < cardsToRemove.length; i++) {
      const cardId = cardsToRemove[i];
      get().unsubscribeOtherListener(`card-${cardId}`);
      get().removeCard(cardId);
    }

    for (let i = 0; i < cardsToAdd.length; i++) {
      const cardId = cardsToAdd[i];
      const unsubscribe = card.subscribe(shopId, cardId, (doc) => {
        if (!doc) return;
        get().setCard(doc);
      });
      get().setOtherListener(`card-${cardId}`, unsubscribe);
    }

    set({ cards: [...currentCards] });
  },
  setCard: (newCard: Card) =>
    set((state) => {
      // Find if card already exists
      const existingCardIndex = state.cards.findIndex(
        (c) => c.id === newCard.id
      );

      if (existingCardIndex >= 0) {
        // Update existing card - create new array with updated card
        const updatedCards = [...state.cards];
        updatedCards[existingCardIndex] = { ...newCard };
        return { cards: updatedCards };
      } else {
        // Add new card - create new array with added card
        return { cards: [...state.cards, { ...newCard }] };
      }
    }),
  removeCard: (id: string) =>
    set((state) => ({
      cards: state.cards.filter((card) => card.id !== id),
    })),
  subscribeActivityFeed: () => {
    const shopId = get().shopId;
    if (!shopId) return;
    const unsubscribe = ActivityFeedRepository.subscribeAll(
      shopId,
      (activityFeed) => set({ activityFeed: activityFeed ?? [] })
    );
    get().setOtherListener("activityFeed", unsubscribe);
  },
  subscribeTimeInContainerMetrics: () => {
    const shopId = get().shopId;
    if (!shopId) return;
    const unsubscribe = TimeInContainerMetricsRepository.subscribe(
      shopId,
      (metrics) => set({ timeInContainerMetrics: metrics })
    );
    get().setOtherListener("timeInContainerMetrics", unsubscribe);
  },
  subscribeConversations: () => {
    const shopId = get().shopId;
    const userId = get().user?.id;

    if (!shopId || !userId) {
      return;
    }
    const unsubscribe = ConversationRepository.subscribeAll(
      shopId,
      userId,
      (conversations) =>
        set({ conversations: conversations, conversationsLoaded: true })
    );
    get().setOtherListener("conversations", unsubscribe);
  },
  subscribeTags: () => {
    const shopId = get().shopId;

    if (!shopId) {
      return;
    }

    const unsubscribe = TagsRepository.subscribe(shopId, (tagDoc) =>
      set({ tags: tagDoc })
    );
    get().setOtherListener("tags", unsubscribe);
  },
});
